import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from "motion/react";
import { X } from "@phosphor-icons/react";
import { boatMapCopy, mapBoats, type MapBoat } from "../../content";
import { useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";
import { BoatLine } from "../art/BoatLine";
import { CompassRose } from "../art/CompassRose";

type BoatMarkProps = {
  boat: MapBoat;
  index: number;
  mx: MotionValue<number>;
  my: MotionValue<number>;
  open: boolean;
  onToggle: () => void;
  reduce: boolean;
  registerButton: (id: string, el: HTMLButtonElement | null) => void;
};

/* One boat on the chart. Pointer drift runs on motion values only. */
function BoatMark({ boat, index, mx, my, open, onToggle, reduce, registerButton }: BoatMarkProps) {
  const dir = index % 2 === 0 ? 1 : -1;
  const fx = ((index % 3) + 1) * 9 * dir;
  const fy = (((index + 1) % 3) + 1) * 7 * -dir;
  const x = useSpring(
    useTransform(mx, (v) => (v - 0.5) * fx),
    { stiffness: 50, damping: 14 },
  );
  const y = useSpring(
    useTransform(my, (v) => (v - 0.5) * fy),
    { stiffness: 50, damping: 14 },
  );

  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${boat.x}%`, top: `${boat.y}%` }}
    >
      <motion.div style={reduce ? undefined : { x, y }}>
        <button
          type="button"
          ref={(el) => registerButton(boat.id, el)}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={open}
          className={`group flex min-h-11 min-w-11 cursor-pointer flex-col items-center gap-1 rounded-md p-2 transition-colors duration-200 hover:bg-night-soft focus-visible:bg-night-soft ${
            open ? "bg-night-soft" : ""
          }`}
        >
          <span
            className="block w-10 motion-safe:animate-bob-slow md:w-14"
            style={{ animationDelay: `${index * 0.65}s` }}
          >
            <BoatLine className="w-full text-night-text" />
          </span>
          <span className="font-mono text-[10px] tracking-wider whitespace-nowrap text-night-text/80 group-hover:text-night-text md:text-xs">
            {boat.name}
          </span>
        </button>
      </motion.div>
    </div>
  );
}

export function BoatMap() {
  const reduce = useReducedMotion() ?? false;
  const { stamp } = useGame();
  const harbourRef = useRef<HTMLDivElement>(null);
  const boatButtons = useRef(new Map<string, HTMLButtonElement>());
  const [openId, setOpenId] = useState<string | null>(null);
  const open = mapBoats.find((b) => b.id === openId) ?? null;

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const registerButton = (id: string, el: HTMLButtonElement | null) => {
    if (el) boatButtons.current.set(id, el);
    else boatButtons.current.delete(id);
  };

  /* Close and hand keyboard focus back to the boat that opened the panel. */
  const closePanel = (refocus: boolean) => {
    setOpenId((current) => {
      if (refocus && current) boatButtons.current.get(current)?.focus();
      return null;
    });
  };

  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePanel(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId]);

  return (
    <section
      id="map"
      className="chart-grid-night overflow-clip bg-night py-24 text-night-text md:py-32"
    >
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <Waypoint station="water" title={boatMapCopy.title} sub={boatMapCopy.sub} onDark />

        <div
          ref={harbourRef}
          onPointerMove={(e) => {
            if (reduce) return;
            const r = harbourRef.current?.getBoundingClientRect();
            if (!r) return;
            mx.set((e.clientX - r.left) / r.width);
            my.set((e.clientY - r.top) / r.height);
          }}
          onClick={() => closePanel(false)}
          className="relative mt-12 h-[30rem] overflow-hidden rounded-md border border-night-line md:h-[34rem]"
        >
          <motion.img
            src="/page-assets/night-harbour.jpg"
            alt="A moonlit harbour where eight lantern-lit boats cross the same water toward a lighthouse."
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-80"
            animate={reduce ? undefined : { scale: [1, 1.025, 1], x: [0, -5, 0] }}
            transition={reduce ? undefined : { duration: 24, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="pointer-events-none absolute inset-0 bg-night/35" />
          <CompassRose className="pointer-events-none absolute top-4 right-4 w-12 text-night-text/50 md:w-16" />

          {mapBoats.map((boat, i) => (
            <BoatMark
              key={boat.id}
              boat={boat}
              index={i}
              mx={mx}
              my={my}
              reduce={reduce}
              registerButton={registerButton}
              open={openId === boat.id}
              onToggle={() => {
                setOpenId(openId === boat.id ? null : boat.id);
                if (openId !== boat.id) stamp("water");
              }}
            />
          ))}

          <AnimatePresence>
            {open && (
              <motion.aside
                key={open.id}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: 10 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                aria-label={open.name}
                /* mobile: bottom sheet; md+: floats next to the clicked boat */
                style={
                  {
                    "--px": `${Math.min(Math.max(open.x - 8, 4), 62)}%`,
                    "--py": `${Math.min(Math.max(open.y + 12, 8), 52)}%`,
                  } as CSSProperties
                }
                className="absolute inset-x-4 bottom-4 z-10 rounded-md border border-night-line bg-night-soft p-6 shadow-lift md:inset-x-auto md:bottom-auto md:left-(--px) md:top-(--py) md:w-80"
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="display-type text-xl">{open.name}</h3>
                  <button
                    type="button"
                    onClick={() => closePanel(true)}
                    aria-label="Close panel"
                    className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-night-line text-night-text transition-colors hover:bg-night"
                  >
                    <X weight="bold" className="size-4" aria-hidden />
                  </button>
                </div>
                <p className="mt-3 text-sm text-night-text/80">{open.blurb}</p>
                <p className="mt-5 font-mono text-xs text-night-text/70">{boatMapCopy.crewLine}</p>
                <p className="mt-1 text-xs text-night-text/70">{boatMapCopy.crewNote}</p>
              </motion.aside>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
